import React, { useState, useRef } from 'react';
import { useModal } from '../../context/ModalContext';
import {
  ClipboardList,
  AlertTriangle,
  Plus,
  CreditCard,
  Lock,
  FileText,
  BarChart3,
  Pencil,
  Check,
  CheckCircle2,
  Download,
} from 'lucide-react';

const Billing = ({ showToast }) => {
  const [currentPlan, setCurrentPlan] = useState({
    name: 'Pro',
    price: '$79',
    sms: '8,000',
    features: [
      'All Growth features',
      'Automation Engine',
      'Two-Way Inbox',
      'Advanced Analytics',
      'Attendance Tracking',
      'Unlimited Admins'
    ]
  });
  
  const [usage, setUsage] = useState({
    used: 2766,
    total: 8000,
    remaining: 5234,
    overageRate: '$0.015 per SMS'
  });
  
  const [billingHistory, setBillingHistory] = useState([
    { id: 1, date: 'Apr 23, 2026', description: 'Pro Plan — April', amount: '$79.00', status: 'Paid' },
    { id: 2, date: 'Mar 23, 2026', description: 'Pro Plan — March', amount: '$79.00', status: 'Paid' },
    { id: 3, date: 'Feb 23, 2026', description: 'Pro Plan — February', amount: '$79.00', status: 'Paid' }
  ]);
  
  const [paymentMethod, setPaymentMethod] = useState({
    type: 'VISA',
    last4: '4242',
    name: 'Grace Chapel Int\'l',
    expiry: '09/28'
  });
  
  const [selectedPlan, setSelectedPlan] = useState('Pro');
  const { openModal, closeModal } = useModal();

  // Refs for payment form
  const cardNumberRef = useRef(null);
  const cardNameRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvvRef = useRef(null);

  const plans = [
    { 
      name: 'Starter', 
      price: '$9', 
      sms: '500', 
      features: ['Broadcasts', 'Birthday Automation', '1 Admin User', 'Basic Dashboard'],
      color: 'var(--blue)'
    },
    { 
      name: 'Growth', 
      price: '$29', 
      sms: '2,000', 
      features: ['All Starter features', 'SMS Polls', 'Groups & Segments', 'Message Templates', '3 Admin Users'],
      color: 'var(--gold)'
    },
    { 
      name: 'Pro', 
      price: '$79', 
      sms: '8,000', 
      features: ['All Growth features', 'Automation Engine', 'Two-Way Inbox', 'Advanced Analytics', 'Attendance Tracking', 'Unlimited Admins'],
      color: 'var(--navy)'
    },
    { 
      name: 'Enterprise', 
      price: 'Custom', 
      sms: 'Unlimited', 
      features: ['All Pro features', 'Multi-Branch/Campus', 'API Access', 'Custom Integrations', 'Dedicated Support'],
      color: 'var(--purple)'
    }
  ];

  // Select plan
  const handleSelectPlan = (plan) => {
    if (plan.name === currentPlan.name) {
      showToast(`✅ Already on the ${plan.name} plan`, 'info');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Switch to {plan.name} Plan</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '12px' }}>
            You are about to switch from <strong>{currentPlan.name}</strong> to <strong>{plan.name}</strong>.
          </p>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              padding: '12px', 
              background: 'var(--cream)', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>Current Plan</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>{currentPlan.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{currentPlan.price}/mo</div>
            </div>
            <div style={{ 
              padding: '12px', 
              background: 'var(--gold-pale)', 
              borderRadius: '8px',
              textAlign: 'center',
              border: '2px solid var(--gold)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>New Plan</div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--gold)' }}>{plan.name}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{plan.price}/mo</div>
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> Your plan will be prorated and the new rate will apply immediately.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              setCurrentPlan(plan);
              setSelectedPlan(plan.name);
              showToast(`✅ Switched to ${plan.name} plan!`, 'success');
              closeModal();
            }}
          >
            Confirm Switch
          </button>
        </div>
      </div>
    );
  };

  // Buy credits
  const handleBuyCredits = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px' }} /> Buy SMS Credits</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '12px' }}>
              Current balance: <strong>{usage.remaining.toLocaleString()} credits</strong>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {[
                { amount: 500, price: '$7.50', perSms: '$0.015' },
                { amount: 1000, price: '$14.00', perSms: '$0.014' },
                { amount: 5000, price: '$65.00', perSms: '$0.013' },
                { amount: 10000, price: '$120.00', perSms: '$0.012' }
              ].map(pkg => (
                <button
                  key={pkg.amount}
                  className="btn btn-ghost btn-sm"
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    width: '100%',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                  onClick={() => {
                    showToast(`💳 Purchased ${pkg.amount} credits for ${pkg.price}`, 'success');
                    setUsage({
                      ...usage,
                      remaining: usage.remaining + pkg.amount,
                      total: usage.total + pkg.amount
                    });
                    closeModal();
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)' }}>
                      {pkg.amount.toLocaleString()} credits
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                      {pkg.perSms} per SMS
                    </div>
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--gold)' }}>
                    {pkg.price}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)'
          }}>
<CreditCard size={14} style={{ verticalAlign: '-2px' }} /> Payment will be processed using your saved card ending in {paymentMethod.last4}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // Update payment method
  const handleUpdatePayment = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CreditCard size={16} style={{ verticalAlign: '-3px' }} /> Update Payment Method</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Card Number</label>
            <input 
              className="form-input" 
              ref={cardNumberRef}
              defaultValue={`•••• •••• •••• ${paymentMethod.last4}`}
              placeholder="1234 5678 9012 3456"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Cardholder Name</label>
            <input 
              className="form-input" 
              ref={cardNameRef}
              defaultValue={paymentMethod.name}
              placeholder="John Doe"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Expiry Date</label>
              <input 
                className="form-input" 
                ref={cardExpiryRef}
                defaultValue={paymentMethod.expiry}
                placeholder="MM/YY"
              />
            </div>
            <div className="form-group">
              <label className="form-label">CVV</label>
              <input 
                className="form-input" 
                ref={cardCvvRef}
                placeholder="123"
                type="password"
                maxLength="4"
              />
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '12px', 
            color: 'var(--blue)' 
          }}>
<Lock size={14} style={{ verticalAlign: '-2px' }} /> Your payment information is secure and encrypted.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              const name = cardNameRef.current?.value || '';
              const expiry = cardExpiryRef.current?.value || '';
              const cardNum = cardNumberRef.current?.value || '';
              
              if (!name.trim()) {
                showToast('Please enter cardholder name', 'error');
                return;
              }
              if (!expiry.trim()) {
                showToast('Please enter expiry date', 'error');
                return;
              }
              
              // Update payment method
              const last4 = cardNum.replace(/\D/g, '').slice(-4) || paymentMethod.last4;
              setPaymentMethod({
                ...paymentMethod,
                name: name,
                expiry: expiry,
                last4: last4
              });
              showToast('💳 Payment method updated!', 'success');
              closeModal();
            }}
          >
            Save Card
          </button>
        </div>
      </div>
    );
  };

  // Download invoice
  const handleDownloadInvoice = (invoice) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><FileText size={16} style={{ verticalAlign: '-3px' }} /> Invoice Download</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            padding: '20px',
            background: 'var(--cream)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><FileText size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)' }}>
              {invoice.description}
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginTop: '4px' }}>
              {invoice.date}
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gold)', marginTop: '8px' }}>
              {invoice.amount}
            </div>
            <div style={{ 
              marginTop: '12px',
              padding: '8px 16px',
              background: 'var(--green-bg)',
              color: 'var(--green)',
              borderRadius: '20px',
              display: 'inline-block',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {invoice.status}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={() => {
              showToast(`📥 ${invoice.description} downloaded!`, 'success');
              closeModal();
            }}
          >
<Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Download PDF
          </button>
        </div>
      </div>
    );
  };

  // View billing details
  const handleViewBillingDetails = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px' }} /> Billing Summary</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Current Plan</span>
              <span style={{ fontWeight: '600' }}>{currentPlan.name} · {currentPlan.price}/mo</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Next Billing</span>
              <span>May 23, 2026</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Payment Method</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {paymentMethod.type} ··· {paymentMethod.last4}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Credits Used</span>
              <span>{usage.used.toLocaleString()} / {usage.total.toLocaleString()}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Credits Remaining</span>
              <span style={{ fontWeight: '600', color: 'var(--green)' }}>{usage.remaining.toLocaleString()}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Overage Rate</span>
              <span>{usage.overageRate}</span>
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>
              <span>Monthly usage</span><span>{usage.used.toLocaleString()} / {usage.total.toLocaleString()}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(usage.used / usage.total) * 100}%`, background: 'var(--green)' }}></div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  const usagePercentage = (usage.used / usage.total) * 100;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Manage your subscription, billing, and SMS credit usage.
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={handleViewBillingDetails}>
            <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> View Summary
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div className="card-title">Current Plan</div>
              <div style={{ fontSize: '28px', fontFamily: 'Playfair Display, serif', fontWeight: '700', color: 'var(--navy)', margin: '8px 0' }}>
                {currentPlan.name} <span style={{ fontSize: '18px', color: 'var(--gold)' }}>{currentPlan.price}/mo</span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Renews May 23, 2026</div>
            </div>
            <span className="badge badge-green" style={{ fontSize: '12px' }}>Active</span>
          </div>
          {[
            ['SMS Credits Included', `${usage.total.toLocaleString()} / month`],
            ['SMS Used This Month', usage.used.toLocaleString()],
            ['Credits Remaining', usage.remaining.toLocaleString()],
            ['Overage Rate', usage.overageRate]
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
              <span style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{l}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace', color: 'var(--navy)' }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>
              <span>Monthly usage</span><span>{usage.used.toLocaleString()} / {usage.total.toLocaleString()}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${usagePercentage}%`, background: usagePercentage > 80 ? 'var(--red)' : 'var(--green)' }}></div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button className="btn btn-gold btn-sm" onClick={handleBuyCredits}>
              + Buy Credits
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleSelectPlan({ name: 'Enterprise', price: 'Custom', sms: 'Unlimited', features: [] })}>
              Upgrade Plan
            </button>
          </div>
        </div>
        
        <div className="card">
          <div className="card-title" style={{ marginBottom: '14px' }}>Payment Method</div>
          <div style={{ background: 'linear-gradient(135deg,var(--navy),var(--navy-light))', borderRadius: '12px', padding: '20px', color: 'white', marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', opacity: 0.5, marginBottom: '16px' }}>{paymentMethod.type}</div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px', letterSpacing: '.15em' }}>
              •••• •••• •••• {paymentMethod.last4}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', fontSize: '12px', opacity: 0.7 }}>
              <span>{paymentMethod.name}</span>
              <span>{paymentMethod.expiry}</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={handleUpdatePayment}>
            <Pencil size={14} style={{ verticalAlign: '-2px' }} /> Update Card
          </button>
          
          <div style={{ marginTop: '16px' }}>
            <div className="card-title" style={{ fontSize: '14px', marginBottom: '10px' }}>Billing History</div>
            {billingHistory.map(invoice => (
              <div key={invoice.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--navy)' }}>{invoice.description}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{invoice.date}</div>
                </div>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', fontWeight: '600' }}>{invoice.amount}</span>
                <span className="badge badge-green" style={{ fontSize: '10px' }}>{invoice.status}</span>
                <button 
                  className="btn btn-ghost btn-sm btn-icon" 
                  onClick={() => handleDownloadInvoice(invoice)}
                  title="Download Invoice"
                >
                  <Download size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: '16px' }}>Available Plans</div>
        <div className="grid-2" style={{ gap: '16px' }}>
          {plans.map(p => {
            const isCurrent = p.name === currentPlan.name;
            return (
              <div 
                key={p.name} 
                className={`plan-card ${isCurrent ? 'selected' : ''}`} 
                style={{ 
                  border: `2px solid ${isCurrent ? 'var(--gold)' : 'var(--border)'}`,
                  borderRadius: '12px', 
                  padding: '20px', 
                  transition: 'all .2s', 
                  cursor: 'pointer',
                  background: isCurrent ? 'var(--gold-pale)' : 'transparent',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent) {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isCurrent && (
                  <div style={{ 
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    background: 'var(--gold)',
                    color: 'var(--navy)',
                    padding: '4px 16px',
                    fontSize: '10px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '.05em',
                    borderRadius: '0 12px 0 12px'
                  }}>
                    Current
                  </div>
                )}
                <div className="plan-name" style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>
                  {p.name}
                </div>
                <div className="plan-price" style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '700', color: 'var(--navy)', margin: '8px 0' }}>
                  {p.price}
                  <span style={{ fontSize: '14px', color: 'var(--text-light)', fontWeight: '400' }}>
                    {p.price !== 'Custom' ? '/mo' : ''}
                  </span>
                </div>
                <div className="plan-sms" style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                  {p.sms} SMS credits/month
                </div>
                <div style={{ margin: '12px 0', borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
                  {p.features.map(f => (
                    <div key={f} className="plan-feature" style={{ fontSize: '12px', color: 'var(--text-mid)', padding: '3px 0' }}>
                      <Check size={12} style={{ verticalAlign: '-2px' }} /> {f}
                    </div>
                  ))}
                </div>
                <button 
                  className={`btn ${isCurrent ? 'btn-ghost' : 'btn-gold'} btn-sm`} 
                  style={{ width: '100%' }}
                  onClick={() => handleSelectPlan(p)}
                  disabled={isCurrent}
                >
                  {isCurrent ? <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Current Plan</> : 'Select Plan'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-gold {
          background: var(--gold-pale);
          color: var(--gold);
        }
        .plan-card {
          transition: all 0.3s ease;
        }
        .plan-card.selected {
          border-color: var(--gold) !important;
          background: var(--gold-pale) !important;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default Billing;